// P1-tooltip: Hover popover that renders a PBI canvas-tooltip page as a
// scaled-down mini canvas anchored near the cursor.
//
// Lifecycle:
//   * _chartShell sets `visible=true` ~300ms after mouseenter and updates
//     `anchor` from mouse coords. Goes false on mouseleave.
//   * `usePage(folder)` fetches the tooltip-page data (react-query caches it,
//     so repeated hovers are instant).
//   * We mount via React Portal to document.body so parent overflow:hidden
//     on the visual card doesn't clip the popover.
//
// Constraints honored from the plan:
//   - Scale factor 0.6
//   - Clamped to 600x400 max
//   - pointer-events: none, z-index 1000
//   - 200ms fade-in

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePage } from "../api/hooks";
import { getVisualRenderer } from "./registry";
import type { VisualPayload } from "../types/pbi";
import { useFilterStore } from "../state/filterStore";

const SCALE = 0.6;
const MAX_W = 600;
const MAX_H = 400;
const FADE_MS = 200;
const ANCHOR_GAP = 16; // gap between cursor and popover edge

interface TooltipPagePopoverProps {
  folder: string;
  anchor: { x: number; y: number };
  visible: boolean;
}

export function TooltipPagePopover({ folder, anchor, visible }: TooltipPagePopoverProps) {
  // P1-tooltip: filters are intentionally NOT threaded through to the tooltip
  // page in this pass — PBI canvas-tooltips can inherit cross-filters, but
  // surfacing that requires per-visual selected-row inference (e.g. the bar
  // clicked supplies a category filter to the tooltip query). Leaving as a
  // future enhancement; the popover renders the tooltip page's static
  // page-level filters which is enough for visual parity.
  const { data, isLoading } = usePage(visible ? folder : undefined, undefined);
  const selectByVisual = useFilterStore((s) => s.selectByVisual);
  const addFilter = useFilterStore((s) => s.addFilter);

  const [mounted, setMounted] = useState(false);
  // Two-stage state so the fade-in runs after the element is in the DOM.
  const [shown, setShown] = useState(false);
  const removeTimer = useRef<number | null>(null);

  useEffect(() => {
    if (visible) {
      if (removeTimer.current) {
        window.clearTimeout(removeTimer.current);
        removeTimer.current = null;
      }
      setMounted(true);
      // Next animation frame -> opacity 1 (CSS transition picks it up).
      const id = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(id);
    } else {
      setShown(false);
      // Unmount after the fade completes so we don't keep an offscreen DOM.
      removeTimer.current = window.setTimeout(() => setMounted(false), FADE_MS + 30);
      return () => {
        if (removeTimer.current) {
          window.clearTimeout(removeTimer.current);
          removeTimer.current = null;
        }
      };
    }
  }, [visible]);

  if (!mounted) return null;

  // Source page dimensions; tooltip pages typically declare 320x240 or 480x300
  // (HSQ Tooltip is 480x300 — see page.json).
  const srcW = data?.page?.width ?? 480;
  const srcH = data?.page?.height ?? 300;
  const popW = Math.min(srcW * SCALE, MAX_W);
  const popH = Math.min(srcH * SCALE, MAX_H);

  // Position the popover so it stays on-screen: prefer below-right of anchor,
  // flip if it would overflow the viewport.
  let left = anchor.x + ANCHOR_GAP;
  let top = anchor.y + ANCHOR_GAP;
  if (typeof window !== "undefined") {
    if (left + popW > window.innerWidth - 8) {
      left = Math.max(8, anchor.x - popW - ANCHOR_GAP);
    }
    if (top + popH > window.innerHeight - 8) {
      top = Math.max(8, anchor.y - popH - ANCHOR_GAP);
    }
  }

  const node = (
    <div
      className="tooltip-page-popover"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        transform: `translate3d(${left}px, ${top}px, 0)`,
        width: popW,
        height: popH,
        zIndex: 1000,
        pointerEvents: "none",
        opacity: shown ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease-out`,
        background: "var(--surface, #fff)",
        borderRadius: 6,
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
      aria-hidden="true"
    >
      <div
        // Inner scaled canvas — mirrors PageView's pattern (transform scale on
        // a fixed-pixel inner box) so visuals retain their PBI absolute
        // positions but the whole canvas is shrunk.
        style={{
          width: srcW,
          height: srcH,
          transform: `scale(${SCALE})`,
          transformOrigin: "top left",
          position: "relative",
        }}
      >
        {isLoading || !data ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "var(--text-muted, #888)",
            }}
          >
            …
          </div>
        ) : (
          [...data.visuals]
            .sort((a, b) => (a.spec.position.z ?? 0) - (b.spec.position.z ?? 0))
            .map((vp) => (
              <MiniVisual
                key={vp.spec.id}
                payload={vp}
                selectByVisual={selectByVisual}
                addFilter={addFilter}
              />
            ))
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

interface MiniVisualProps {
  payload: VisualPayload;
  selectByVisual: ReturnType<typeof useFilterStore.getState>["selectByVisual"];
  addFilter: ReturnType<typeof useFilterStore.getState>["addFilter"];
}

function MiniVisual({ payload, selectByVisual, addFilter }: MiniVisualProps) {
  // P1-tooltip: reuse the same visual registry so all 29 PBI types render.
  // pointer-events on the parent popover is `none`, so onFilter clicks won't
  // actually fire — the popover is a read-only preview.
  const Renderer = getVisualRenderer(payload.spec.type);
  const { x, y, width, height, z } = payload.spec.position;
  const filters = selectByVisual(payload.spec.id);
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        width,
        height,
        zIndex: z,
      }}
    >
      <Renderer
        spec={payload.spec}
        data={payload.data}
        filters={filters}
        onFilter={addFilter}
      />
    </div>
  );
}
